import type { ReactNode } from "react";

export type StripTier = {
  id: string;
  name: string;
  logo_size?: string | null;
};

export type StripPartner = {
  id: string;
  name: string;
  logo_url?: string | null;
  website?: string | null;
  tier_id?: string | null;
  logo_scale?: number | null;
  container_scale?: number | null;
};

const sizes: Record<string, string> = {
  xl: "h-28 w-auto max-w-[min(88vw,34rem)] md:h-40",
  lg: "h-24 w-auto max-w-[min(80vw,26rem)] md:h-32",
  md: "h-20 w-auto max-w-[min(70vw,19rem)] md:h-24",
  sm: "h-14 w-auto max-w-[min(60vw,13rem)] md:h-16",
};

const compactSizes: Record<string, string> = {
  xl: "h-20 w-auto max-w-[min(80vw,24rem)] md:h-28",
  lg: "h-16 w-auto max-w-[min(70vw,18rem)] md:h-24",
  md: "h-14 w-auto max-w-[min(60vw,13rem)] md:h-18",
  sm: "h-10 w-auto max-w-[min(50vw,9rem)] md:h-12",
};

export function isCompactTier(name: string) {
  const n = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return n.includes("sprijin") || n.includes("media");
}

export function PartnersStripView({
  tiers,
  partners,
  renderLogo,
  interactive = true,
  className = "border-t bg-white py-16",
  containerClassName = "mx-auto max-w-6xl space-y-14 px-6",
}: {
  tiers: StripTier[];
  partners: StripPartner[];
  renderLogo?: (partner: StripPartner, node: ReactNode) => ReactNode;
  interactive?: boolean;
  className?: string;
  containerClassName?: string;
}) {
  return (
    <section className={className}>
      <div className={containerClassName}>
        {tiers.map((tier) => {
          const list = partners.filter((p) => p.tier_id === tier.id && p.logo_url);
          if (list.length === 0) return null;
          const palette = isCompactTier(tier.name) ? compactSizes : sizes;
          const h = palette[tier.logo_size ?? "md"] ?? palette["md"];

          return (
            <div key={tier.id} className="py-2">
              <div className="flex justify-center">
                <span className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-center text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-sm">
                  {tier.name}
                </span>
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-8">
                {list.map((p) => {
                  const logoScale = Number(p.logo_scale ?? 1) || 1;
                  const containerScale = Number(p.container_scale ?? 1) || 1;
                  const img = (
                    <span
                      style={{ zoom: containerScale }}
                      className={`${h} flex items-center justify-center overflow-visible`}
                    >
                      <img
                        src={p.logo_url!}
                        alt={p.name}
                        loading="lazy"
                        draggable={false}
                        style={{ transform: `scale(${logoScale})` }}
                        className={`max-h-full max-w-full origin-center object-contain transition ${
                          interactive ? "hover:scale-105" : ""
                        }`}
                      />
                    </span>
                  );
                  if (renderLogo) return renderLogo(p, img);
                  return p.website ? (
                    <a key={p.id} href={p.website} target="_blank" rel="noopener noreferrer">
                      {img}
                    </a>
                  ) : (
                    <div key={p.id}>{img}</div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
